import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  Paper,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { useEffect, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Link, useParams } from 'react-router';

import type { AppError } from '../../api/contract';
import { useAuth } from '../../auth/AuthProvider';
import type { AuthMode } from '../../auth/types';
import type { DataSource } from '../../config/capabilities';
import { useRepository } from '../../data/RepositoryProvider';
import { useResource } from '../../hooks/useResource';
import { DataSourceBadge } from '../../shared/data/DataSourceBadge';
import { PageState } from '../../shared/data/PageState';
import { DetailHeader } from '../../shared/detail/DetailHeader';
import { MetadataList } from '../../shared/detail/MetadataList';
import { formatTimestamp, type TimeLanguage } from '../../shared/format/time';
import { MutationError, toDialogError } from '../partners/PartnerFormDialog';
import { toPageError } from '../partners/PartnerListPage';
import { AdminUserAccessDialog } from './AdminUserAccessDialog';
import { AdminUserStatusChip } from './AdminUserListPage';
import { adminUsersRepositoryToken } from './repository';
import type { AdminUserDetail, AdminUserStatus } from './types';

export function AdminUserDetailPage({
  authMode,
  dataSource,
}: {
  readonly authMode: AuthMode;
  readonly dataSource: DataSource;
}) {
  const { adminUserId = '' } = useParams();
  const repository = useRepository(adminUsersRepositoryToken);
  const auth = useAuth();
  const { i18n, t } = useTranslation();
  const language: TimeLanguage = i18n.language.startsWith('th') ? 'th' : 'en';
  const [refresh, setRefresh] = useState(0);
  const [profileRefresh, setProfileRefresh] = useState(0);
  const [accessOpen, setAccessOpen] = useState(false);
  const [statusOpen, setStatusOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const resource = useResource(
    (signal) => repository.getUser(adminUserId, signal),
    [adminUserId, refresh, repository],
    () => false,
  );
  const principal = auth.status === 'authenticated' ? auth.principal : null;
  const isSelf = principal?.actor.id === adminUserId;
  const canManage = principal?.permissions.includes('BACKOFFICE_USER_MANAGE') === true;
  const reload = () => setRefresh((value) => value + 1);
  const profileId = resource.data?.permissionProfile?.id ?? null;
  const profile = useResource(
    (signal) =>
      profileId === null ? Promise.resolve(null) : repository.getProfile(profileId, signal),
    [profileId, profileRefresh, repository],
    () => false,
  );
  return (
    <PageState
      error={toPageError(resource.error, t('adminUsers.errors.detail'))}
      onRetry={reload}
      status={resource.status}
    >
      {resource.data === null ? null : (
        <Stack spacing={2}>
          <DetailHeader
            actions={
              canManage ? (
                <>
                  <Button onClick={() => setEditOpen(true)} variant="outlined">
                    {t('adminUsers.actions.edit')}
                  </Button>
                  <Button disabled={isSelf} onClick={() => setAccessOpen(true)} variant="contained">
                    {t('adminUsers.actions.editAccess')}
                  </Button>
                  <Button
                    color="warning"
                    disabled={isSelf || resource.data.status === 'CLOSED'}
                    onClick={() => setStatusOpen(true)}
                    variant="contained"
                  >
                    {t('adminUsers.actions.changeStatus')}
                  </Button>
                </>
              ) : undefined
            }
            backTo="/admin-users"
            dataSource={dataSource}
            subtitle={resource.data.username ?? t('common.notAvailable')}
            title={resource.data.displayName}
          />
          {isSelf ? <Alert severity="info">{t('adminUsers.access.selfBlocked')}</Alert> : null}
          <AdminUserMetadata language={language} user={resource.data} />
          <PermissionGrants
            error={profile.error}
            hasProfile={profileId !== null}
            onRetry={() => setProfileRefresh((value) => value + 1)}
            permissions={profile.data?.permissions ?? null}
            status={profile.status}
          />
          {canManage ? <ResetMfaSection authMode={authMode} /> : null}
          <AdminUserAccessDialog
            onClose={() => setAccessOpen(false)}
            onSaved={reload}
            open={accessOpen}
            user={resource.data}
          />
          <EditUserDialog
            onClose={() => setEditOpen(false)}
            onSaved={reload}
            open={editOpen}
            user={resource.data}
          />
          <UserStatusDialog
            onClose={() => setStatusOpen(false)}
            onSaved={reload}
            open={statusOpen}
            user={resource.data}
          />
        </Stack>
      )}
    </PageState>
  );
}

function AdminUserMetadata({
  language,
  user,
}: {
  readonly language: TimeLanguage;
  readonly user: AdminUserDetail;
}) {
  const { t } = useTranslation();
  const lastLogin =
    user.lastLoginAt === null ? null : formatTimestamp(user.lastLoginAt, 'Asia/Bangkok', language);
  const updated = formatTimestamp(user.updatedAt, 'Asia/Bangkok', language);
  return (
    <Paper component="section" sx={{ p: 2 }} variant="outlined">
      <MetadataList
        items={[
          {
            label: t('adminUsers.fields.username'),
            value: user.username ?? t('common.notAvailable'),
          },
          { label: t('adminUsers.fields.email'), value: user.email ?? t('common.notAvailable') },
          { label: t('adminUsers.fields.role'), value: user.role },
          {
            label: t('adminUsers.fields.status'),
            value: <AdminUserStatusChip status={user.status} />,
          },
          {
            label: t('adminUsers.fields.partnerId'),
            value: user.partnerId ?? t('common.notAvailable'),
          },
          {
            label: t('adminUsers.fields.agentId'),
            value: user.agentId ?? t('common.notAvailable'),
          },
          {
            label: t('adminUsers.fields.profile'),
            value:
              user.permissionProfile === null ? (
                t('common.notAvailable')
              ) : (
                <Link to="/permission-profiles">{user.permissionProfile.name}</Link>
              ),
          },
          {
            label: t('adminUsers.fields.lastLogin'),
            value:
              lastLogin === null
                ? t('common.notAvailable')
                : `${lastLogin.display} (${lastLogin.originalOffset})`,
          },
          {
            label: t('adminUsers.fields.updatedAt'),
            value: `${updated.display} (${updated.originalOffset})`,
          },
        ]}
      />
    </Paper>
  );
}

function PermissionGrants({
  error,
  hasProfile,
  onRetry,
  permissions,
  status,
}: {
  readonly error: unknown | null;
  readonly hasProfile: boolean;
  readonly onRetry: () => void;
  readonly permissions: readonly string[] | null;
  readonly status: 'loading' | 'success' | 'empty' | 'error';
}) {
  const { t } = useTranslation();
  return (
    <Paper component="section" sx={{ p: 2 }} variant="outlined">
      <Stack spacing={2}>
        <div>
          <Typography component="h2" variant="subtitle1">
            {t('adminUsers.access.roleGrants')}
          </Typography>
          <Typography color="text.secondary">
            {t('adminUsers.access.roleGrantsUnavailable')}
          </Typography>
        </div>
        <div>
          <Typography component="h2" variant="subtitle1">
            {t('adminUsers.access.profileGrants')}
          </Typography>
          {hasProfile ? (
            <PageState
              error={toPageError(error, t('adminUsers.errors.profileDetail'))}
              onRetry={onRetry}
              status={status}
            >
              <Typography>
                {permissions === null || permissions.length === 0
                  ? t('common.notAvailable')
                  : permissions.join(', ')}
              </Typography>
            </PageState>
          ) : (
            <Typography color="text.secondary">{t('adminUsers.access.noProfileGrants')}</Typography>
          )}
        </div>
      </Stack>
    </Paper>
  );
}

/**
 * There is no MFA-reset endpoint in the current OpenAPI contract. Under DEV Mock Auth this
 * renders a clearly-labeled Mock Data affordance that only records local acknowledgement — it
 * never calls the repository. Under Cognito, MFA is explicitly identity-provider-managed and no
 * button is rendered at all.
 */
function ResetMfaSection({ authMode }: { readonly authMode: AuthMode }) {
  const { t } = useTranslation();
  const [acknowledged, setAcknowledged] = useState(false);
  return (
    <Paper component="section" sx={{ p: 2 }} variant="outlined">
      <Stack spacing={1.5}>
        <Stack
          direction="row"
          spacing={1}
          sx={{ alignItems: 'center', justifyContent: 'space-between' }}
        >
          <Typography component="h2" variant="subtitle1">
            {t('adminUsers.security.title')}
          </Typography>
          <DataSourceBadge source={authMode === 'mock' ? 'mock' : 'unavailable'} />
        </Stack>
        {authMode === 'mock' ? (
          <>
            <Typography color="text.secondary">
              {t('adminUsers.security.mockDescription')}
            </Typography>
            <Button
              onClick={() => setAcknowledged(true)}
              sx={{ alignSelf: 'flex-start' }}
              variant="outlined"
            >
              {t('adminUsers.security.resetMfa')}
            </Button>
            {acknowledged ? (
              <Alert severity="info">{t('adminUsers.security.mockAcknowledged')}</Alert>
            ) : null}
          </>
        ) : (
          <Alert severity="info">{t('adminUsers.security.identityProviderManaged')}</Alert>
        )}
      </Stack>
    </Paper>
  );
}

interface EditValues {
  readonly displayName: string;
  readonly reason: string;
}
function EditUserDialog({
  onClose,
  onSaved,
  open,
  user,
}: {
  readonly onClose: () => void;
  readonly onSaved: () => void;
  readonly open: boolean;
  readonly user: AdminUserDetail;
}) {
  const repository = useRepository(adminUsersRepositoryToken);
  const { t } = useTranslation();
  const guard = useRef(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<Pick<AppError, 'message' | 'requestId'> | null>(null);
  const {
    formState: { errors },
    handleSubmit,
    register,
    reset,
  } = useForm<EditValues>({ defaultValues: { displayName: user.displayName, reason: '' } });
  useEffect(() => {
    if (open) {
      reset({ displayName: user.displayName, reason: '' });
      setError(null);
      setBusy(false);
      guard.current = false;
    }
  }, [open, reset, user.displayName]);
  const required = {
    required: t('adminUsers.validation.required'),
    validate: (value: string) => value.trim().length > 0 || t('adminUsers.validation.required'),
  };
  const close = () => {
    if (!guard.current) onClose();
  };
  const submit = handleSubmit(async (value) => {
    if (guard.current) return;
    guard.current = true;
    setBusy(true);
    setError(null);
    try {
      await repository.updateUser(user.id, {
        displayName: value.displayName.trim(),
        reason: value.reason.trim(),
      });
      onSaved();
      onClose();
    } catch (cause) {
      setError(toDialogError(cause, t('adminUsers.errors.mutation')));
    } finally {
      guard.current = false;
      setBusy(false);
    }
  });
  return (
    <Dialog fullWidth onClose={close} open={open}>
      <DialogTitle>{t('adminUsers.actions.edit')}</DialogTitle>
      <DialogContent>
        <Stack
          component="form"
          id="edit-admin-user-form"
          onSubmit={submit}
          spacing={2}
          sx={{ pt: 1 }}
        >
          {error === null ? null : <MutationError error={error} />}
          <TextField
            error={!!errors.displayName}
            helperText={errors.displayName?.message}
            label={t('adminUsers.fields.displayName')}
            required
            {...register('displayName', required)}
          />
          <TextField
            error={!!errors.reason}
            helperText={errors.reason?.message}
            label={t('adminUsers.fields.reason')}
            multiline
            required
            {...register('reason', required)}
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button disabled={busy} onClick={close}>
          {t('actions.cancel')}
        </Button>
        <Button form="edit-admin-user-form" loading={busy} type="submit" variant="contained">
          {t('actions.save')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

interface StatusValues {
  readonly status: AdminUserStatus;
  readonly reason: string;
}
function UserStatusDialog({
  onClose,
  onSaved,
  open,
  user,
}: {
  readonly onClose: () => void;
  readonly onSaved: () => void;
  readonly open: boolean;
  readonly user: AdminUserDetail;
}) {
  const repository = useRepository(adminUsersRepositoryToken);
  const { t } = useTranslation();
  const guard = useRef(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<Pick<AppError, 'message' | 'requestId'> | null>(null);
  const {
    formState: { errors },
    handleSubmit,
    register,
    reset,
  } = useForm<StatusValues>({ defaultValues: { status: user.status, reason: '' } });
  useEffect(() => {
    if (open) {
      reset({ status: user.status, reason: '' });
      setError(null);
      guard.current = false;
      setBusy(false);
    }
  }, [open, reset, user.status]);
  const close = () => {
    if (!guard.current) onClose();
  };
  const submit = handleSubmit(async (value) => {
    if (guard.current) return;
    guard.current = true;
    setBusy(true);
    setError(null);
    try {
      await repository.changeStatus(user.id, { status: value.status, reason: value.reason.trim() });
      onSaved();
      onClose();
    } catch (cause) {
      setError(toDialogError(cause, t('adminUsers.errors.mutation')));
    } finally {
      guard.current = false;
      setBusy(false);
    }
  });
  return (
    <Dialog fullWidth onClose={close} open={open}>
      <DialogTitle>{t('adminUsers.actions.changeStatus')}</DialogTitle>
      <DialogContent>
        <Stack
          component="form"
          id="admin-user-status-form"
          onSubmit={submit}
          spacing={2}
          sx={{ pt: 1 }}
        >
          {error === null ? null : <MutationError error={error} />}
          <Alert severity="warning">
            {t('adminUsers.status.impact', { name: user.displayName, status: user.status })}
          </Alert>
          <TextField label={t('adminUsers.fields.proposedStatus')} select {...register('status')}>
            {['INVITED', 'ACTIVE', 'SUSPENDED', 'CLOSED'].map((status) => (
              <MenuItem key={status} value={status}>
                {status}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            error={!!errors.reason}
            helperText={errors.reason?.message}
            label={t('adminUsers.fields.reason')}
            multiline
            required
            {...register('reason', {
              required: t('adminUsers.validation.required'),
              validate: (value) => value.trim().length > 0 || t('adminUsers.validation.required'),
            })}
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button disabled={busy} onClick={close}>
          {t('actions.cancel')}
        </Button>
        <Button form="admin-user-status-form" loading={busy} type="submit" variant="contained">
          {t('actions.confirm')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
